/* ==========================================================================
   Statement Denim — motion runtime

   Everything per-frame runs in ONE requestAnimationFrame loop: the custom
   cursor, parallax, magnetic buttons, the scroll-driven hero turntable and the
   nav scroll state. Reveals use an IntersectionObserver and fire once.

   All motion is gated on prefers-reduced-motion; the cursor, magnets and other
   hover affordances are gated on (pointer: fine).
   ========================================================================== */
(function () {
  'use strict';

  var REDUCE = matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE = matchMedia('(pointer: fine)').matches;
  var EASE = 'cubic-bezier(.16,1,.3,1)';

  function clamp(v, a, b) {
    return Math.min(b, Math.max(a, v));
  }

  /* ── shared frame loop ───────────────────────────────────────────────── */

  var frameJobs = [];
  var running = false;

  function onFrame(fn) {
    frameJobs.push(fn);
    if (!running) {
      running = true;
      requestAnimationFrame(tick);
    }
  }

  function tick() {
    var vh = innerHeight;
    var vw = innerWidth;
    for (var i = 0; i < frameJobs.length; i++) {
      try {
        frameJobs[i](vw, vh);
      } catch (e) {
        /* one broken job must not kill the loop */
      }
    }
    requestAnimationFrame(tick);
  }

  var SD = (window.SD = window.SD || {});
  SD.onFrame = onFrame;
  SD.reduce = REDUCE;
  SD.fine = FINE;
  SD.clamp = clamp;

  /* pointer position, shared by the cursor and the magnets */
  var mx = -300;
  var my = -300;
  if (FINE) {
    addEventListener(
      'pointermove',
      function (e) {
        mx = e.clientX;
        my = e.clientY;
      },
      { passive: true }
    );
  }

  /* ── custom cursor ───────────────────────────────────────────────────── */

  function initCursor() {
    if (!FINE || REDUCE) return;

    var el = document.querySelector('[data-sd-cursor]');
    if (!el) return;

    var label = el.querySelector('.sd-cursor__label');
    var tag = el.querySelector('.sd-cursor__tag');
    var cx = -300;
    var cy = -300;
    var current = null;
    var framed = null; /* the element the border is currently wrapped around */

    document.documentElement.classList.add('sd-cursor-on');
    el.classList.add('is-live');

    /* How far outside the control the border sits. */
    var FRAME_PAD = 5;

    /* Only small, discrete controls get a border. Around a product card or a
       collection look the border is hard to read and makes the page awkward to
       move through, and around the hero or the turntable stage it would span
       most of the viewport. Everything above this height keeps the chip.
       Real controls sit well under it: a nav link is 17px, the bag 35px, a
       full-width buy button 48px. A card is 436px and a look 620px. */
    var MAX_FRAME_H = 120;
    var FRAMEABLE = 'a, button, summary, select, label, [role="button"]';

    function canFrame(host) {
      if (!host.matches || !host.matches(FRAMEABLE)) return false;
      if (host.getAttribute('data-sd-cur-frame') === 'false') return false;
      var r = host.getBoundingClientRect();
      if (!r.width || !r.height) return false;
      if (r.height > MAX_FRAME_H) return false;
      /* innerWidth can read 0 in a collapsed or offscreen viewport, which would
         otherwise disable framing entirely, so fall back to the document
         element before giving up on the width test. */
      var vw = innerWidth || document.documentElement.clientWidth || 0;
      return !vw || r.width <= vw * 0.86;
    }

    function place() {
      if (!framed) return;
      var r = framed.getBoundingClientRect();
      el.style.width = r.width + FRAME_PAD * 2 + 'px';
      el.style.height = r.height + FRAME_PAD * 2 + 'px';
      el.style.transform =
        'translate3d(' + (r.left - FRAME_PAD) + 'px,' + (r.top - FRAME_PAD) + 'px,0)';
      /* no room for the tag above — the sticky header is the usual case */
      el.classList.toggle('is-tagBelow', r.top < 58);
    }

    function frame(host, verb) {
      framed = host;
      tag.textContent = verb;
      el.classList.remove('is-wide');
      el.classList.add('is-frame');
      place();
    }

    function unframe() {
      if (!framed) return;
      /* Hand the lerp the border's own corner as its starting point, so the
         square flies back to the pointer instead of teleporting to it. */
      var r = framed.getBoundingClientRect();
      cx = r.left - FRAME_PAD + 6;
      cy = r.top - FRAME_PAD + 6;
      framed = null;
      el.classList.remove('is-frame', 'is-tagBelow');
      el.style.width = '';
      el.style.height = '';
    }

    function wide(text) {
      unframe();
      label.textContent = text;
      el.classList.add('is-wide');
    }

    function narrow() {
      unframe();
      el.classList.remove('is-wide');
    }

    /* A framed control moves with the page, and the border is fixed, so the
       rect has to be re-read. Done on scroll and resize rather than every
       frame: this forces layout, and while framed nothing else moves it. */
    addEventListener('scroll', place, { passive: true });
    addEventListener('resize', place);

    /* Crimson-on-crimson and crimson-on-navy are invisible, so dark fields flip
       the cursor to bone. closest() means a nested light element — the white
       poster CTA — flips it back without any extra bookkeeping. */
    document.querySelectorAll('.sd-section--dark, .sd-poster, .sd-footer').forEach(function (el) {
      if (!el.hasAttribute('data-sd-cursor-tone')) el.setAttribute('data-sd-cursor-tone', 'light');
    });
    document.querySelectorAll('.sd-btn--light').forEach(function (el) {
      if (!el.hasAttribute('data-sd-cursor-tone')) el.setAttribute('data-sd-cursor-tone', 'dark');
    });

    function applyTone(target) {
      var host = target && target.closest ? target.closest('[data-sd-cursor-tone]') : null;
      var light = !!host && host.getAttribute('data-sd-cursor-tone') === 'light';
      el.classList.toggle('is-light', light);
    }

    /* delegated so labels work on markup added later (drawer, filtered grid) */
    document.addEventListener('pointerover', function (e) {
      applyTone(e.target);
      var host = e.target.closest ? e.target.closest('[data-sd-cur]') : null;
      if (host === current) return;
      current = host;
      var text = host && host.getAttribute('data-sd-cur');
      if (!text) narrow();
      else if (canFrame(host)) frame(host, text);
      else wide(text);
    });

    document.addEventListener('pointerout', function (e) {
      if (!e.relatedTarget) {
        current = null;
        narrow();
      }
    });

    onFrame(function () {
      /* The lerp keeps running while framed so that cx/cy stay live, but the
         transform belongs to CSS in that mode — writing it here every frame
         would kill the transition that carries the border onto the control. */
      cx += (mx - cx) * 0.24;
      cy += (my - cy) * 0.24;
      if (framed) return;
      el.style.transform = 'translate3d(' + (cx - 6) + 'px,' + (cy - 6) + 'px,0)';
    });
  }

  /* ── reveal on scroll ────────────────────────────────────────────────── */

  function initReveals() {
    var items = document.querySelectorAll('[data-sd-rise]:not(.sd-in):not([data-sd-rise-manual])');
    if (!items.length) return;

    if (REDUCE || !('IntersectionObserver' in window)) {
      items.forEach(function (el) {
        el.classList.add('sd-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var delay = +(entry.target.getAttribute('data-sd-delay') || 0);
          setTimeout(function () {
            entry.target.classList.add('sd-in');
          }, delay);
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    items.forEach(function (el) {
      io.observe(el);
    });
  }

  SD.revealNow = function (list, step) {
    var gap = step == null ? 38 : step;
    list.forEach(function (el, i) {
      if (REDUCE) {
        el.classList.add('sd-in');
        return;
      }
      el.classList.remove('sd-in');
      setTimeout(function () {
        el.classList.add('sd-in');
      }, 40 + i * gap);
    });
  };

  /* ── magnetic buttons ────────────────────────────────────────────────── */

  function initMagnets() {
    if (!FINE || REDUCE) return;

    var magnets = [].slice.call(document.querySelectorAll('[data-sd-mag]'));
    if (!magnets.length) return;

    onFrame(function () {
      for (var i = 0; i < magnets.length; i++) {
        var el = magnets[i];
        var r = el.getBoundingClientRect();
        if (!r.width) continue;
        var sx = +(el.getAttribute('data-sd-mag-x') || 0.13);
        var sy = +(el.getAttribute('data-sd-mag-y') || 0.13);
        var dx = mx - (r.left + r.width / 2);
        var dy = my - (r.top + r.height / 2);
        var inReach = Math.hypot(dx, dy) < Math.max(r.width, r.height);
        el.style.transform = inReach
          ? 'translate3d(' + dx * sx + 'px,' + dy * sy + 'px,0)'
          : 'translate3d(0,0,0)';
      }
    });
  }

  /* ── parallax ────────────────────────────────────────────────────────── */

  function initParallax() {
    if (REDUCE) return;

    var items = [].slice.call(document.querySelectorAll('[data-sd-para]'));
    if (!items.length) return;

    onFrame(function (vw, vh) {
      for (var i = 0; i < items.length; i++) {
        var el = items[i];
        var speed = +(el.getAttribute('data-sd-speed') || 0);
        var range = +(el.getAttribute('data-sd-range') || 160);
        var offset = +(el.getAttribute('data-sd-offset') || 0);
        /* measure the box the element moves inside, not the element itself,
           so its own transform doesn't feed back into the reading */
        var box = el.getAttribute('data-sd-para') === 'child' ? el.parentElement : el;
        var r = box.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) continue;
        var q = (r.top + r.height / 2 - vh / 2) / vh;
        el.style.transform = 'translate3d(0,' + (q * speed * range + offset) + 'px,0)';
      }
    });
  }

  /* ── nav scroll state (homepage: transparent → bone + rule) ──────────── */

  function initNav() {
    var nav = document.querySelector('[data-sd-nav-scroll]');
    if (!nav) return;

    var on = false;
    onFrame(function () {
      var next = scrollY > 24;
      if (next === on) return;
      on = next;
      nav.classList.toggle('is-solid', on);
    });
  }

  /* ── intro curtain ───────────────────────────────────────────────────── */

  function initCurtain() {
    var curtain = document.querySelector('[data-sd-curtain]');
    if (!curtain) return;

    if (REDUCE) {
      curtain.remove();
      return;
    }

    /* "Once per visit" — the curtain runs site-wide, so without this every
       click through the site costs another 1.4s of cover. */
    if (curtain.getAttribute('data-once') === 'once') {
      try {
        if (sessionStorage.getItem('sd-curtain-seen')) {
          curtain.remove();
          return;
        }
        sessionStorage.setItem('sd-curtain-seen', '1');
      } catch (e) {
        /* private mode or storage disabled — just play it */
      }
    }

    var mark = curtain.querySelector('[data-sd-curtain-mark]');
    var panels = [].slice.call(curtain.querySelectorAll('[data-sd-panel]'));
    var html = document.documentElement;
    var prev = html.style.overflow;

    html.style.overflow = 'hidden';

    setTimeout(function () {
      if (mark) mark.style.opacity = '0';
    }, 400);

    panels.forEach(function (p, i) {
      setTimeout(function () {
        p.style.transform = 'translateY(-101%)';
      }, 490 + i * 46);
    });

    setTimeout(function () {
      curtain.remove();
      html.style.overflow = prev;
    }, 1440);
  }

  /* ── accordion ───────────────────────────────────────────────────────── */

  function initAccordion() {
    document.querySelectorAll('[data-sd-acc]').forEach(function (btn) {
      var body = btn.parentElement.querySelector('[data-sd-acc-body]');
      if (!body) return;

      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
        body.style.maxHeight = open ? '0px' : body.scrollHeight + 'px';
      });
    });
  }

  /* ── turntable ───────────────────────────────────────────────────────────
     <sd-turntable> stacks its frames absolutely and shows one at a time.

       data-mode="drag"   pointer drag rotates it (PDP stage, homepage rail)
       data-mode="scroll" scroll progress through data-pin drives it (hero)
       data-per-frame     pixels of pointer travel per frame (default 40)
       data-scrub         selector for a scrub track that mirrors / sets it
     ──────────────────────────────────────────────────────────────────────── */

  var Turntable = (function () {
    function Turntable() {
      return Reflect.construct(HTMLElement, [], Turntable);
    }

    Turntable.prototype = Object.create(HTMLElement.prototype);
    Turntable.prototype.constructor = Turntable;
    Object.setPrototypeOf(Turntable, HTMLElement);

    /* When the section offers several sequences, one is chosen here rather than
       server-side: only the chosen sequence's images are ever requested, so the
       variety costs nothing in bandwidth. On the homepage the intro curtain
       covers the viewport for 1.44s, which is ample cover for the swap. */
    Turntable.prototype._buildFromSequences = function () {
      var payload = this.querySelector('[data-sd-sequences]');
      if (!payload) return;

      var sequences;
      try {
        sequences = JSON.parse(payload.textContent);
      } catch (e) {
        return;
      }
      if (!sequences || !sequences.length) return;

      var pick = sequences[Math.floor(Math.random() * sequences.length)];
      if (!pick || !pick.frames || !pick.frames.length) return;

      this.dataset.sequence = pick.alt || '';
      var html = pick.frames
        .map(function (src, i) {
          return (
            '<img class="sd-stage__frame' + (i === 0 ? ' is-on' : '') + '"' +
            ' src="' + src + '"' +
            ' width="' + (pick.width || 1067) + '" height="' + (pick.height || 1600) + '"' +
            ' alt="' + (pick.alt || '').replace(/"/g, '') + ', view ' + (i + 1) + ' of ' + pick.frames.length + '"' +
            ' draggable="false" loading="eager"' +
            ' fetchpriority="' + (i === 0 ? 'high' : 'low') + '"' +
            ' decoding="async" style="object-fit:contain">'
          );
        })
        .join('');
      payload.insertAdjacentHTML('beforebegin', html);
    };

    Turntable.prototype.connectedCallback = function () {
      if (this._ready) return;
      this._ready = true;

      this._buildFromSequences();
      this.frames = [].slice.call(this.querySelectorAll('.sd-stage__frame'));
      this.count = this.frames.length;
      if (!this.count) return;

      this.mode = this.dataset.mode || 'drag';
      this.perFrame = +(this.dataset.perFrame || 40);
      this.deg = this.querySelector('[data-sd-deg]');
      this.hint = this.querySelector('[data-sd-hint]');
      this.turn = 0;
      this.shown = 0;
      this.enabled = true;
      this.frames[0].classList.add('is-on');

      /* Eager loading gets the bytes; this gets them decoded. Without it the
         first pass through the sequence flashes once per frame, as each one
         decodes at the moment it first becomes visible. */
      this.frames.forEach(function (frame) {
        if (typeof frame.decode !== 'function') return;
        frame.decode().catch(function () {
          /* a frame that fails to decode simply paints normally */
        });
      });

      var scrubSel = this.dataset.scrub;
      this.scrub = scrubSel ? document.querySelector(scrubSel) : null;
      this.scrubFill = this.scrub ? this.scrub.querySelector('.sd-scrub__fill') : null;

      if (this.mode === 'drag') this._bindDrag();
      if (this.scrub) this._bindScrub();
      if (this.mode === 'scroll') this._bindScroll();

      this.paint();
    };

    /* fraction of a full turn, 0 → 1 */
    Turntable.prototype.fraction = function () {
      var n = this.count - 1;
      if (n <= 0) return 0;
      return (((this.turn / n) % 1) + 1) % 1;
    };

    Turntable.prototype.paint = function () {
      var i = ((Math.round(this.turn) % this.count) + this.count) % this.count;
      if (i !== this.shown) {
        this.frames[this.shown].classList.remove('is-on');
        this.frames[i].classList.add('is-on');
        this.shown = i;
      }

      var pct = this.fraction();
      if (this.deg) {
        this.deg.textContent = String(Math.round(pct * 360)).padStart(3, '0') + '°';
      }
      if (this.scrubFill) {
        this.scrubFill.style.width = (pct * 100).toFixed(1) + '%';
      }
    };

    Turntable.prototype.setFraction = function (f) {
      this.turn = clamp(f, 0, 1) * (this.count - 1);
      this.paint();
    };

    /* the PDP hands drag back and forth as thumbnails are selected */
    Turntable.prototype.setEnabled = function (on) {
      this.enabled = !!on;
    };

    Turntable.prototype._bindDrag = function () {
      var self = this;
      var down = false;
      var px = 0;

      this.addEventListener('pointerdown', function (e) {
        if (!self.enabled) return;
        down = true;
        px = e.clientX;
        try {
          self.setPointerCapture(e.pointerId);
        } catch (err) {
          /* capture is best-effort */
        }
        e.preventDefault();
        if (self.hint) self.hint.style.animation = 'none';
      });

      this.addEventListener('pointermove', function (e) {
        if (!down || !self.enabled) return;
        self.turn += (e.clientX - px) / self.perFrame;
        px = e.clientX;
        self.paint();
      });

      function up() {
        down = false;
      }

      this.addEventListener('pointerup', up);
      this.addEventListener('pointercancel', up);
      this.addEventListener('lostpointercapture', up);
    };

    Turntable.prototype._bindScrub = function () {
      var self = this;
      var track = this.scrub;
      var down = false;

      function set(e) {
        if (!self.enabled) return;
        var r = track.getBoundingClientRect();
        self.setFraction((e.clientX - r.left) / r.width);
      }

      track.addEventListener('pointerdown', function (e) {
        down = true;
        try {
          track.setPointerCapture(e.pointerId);
        } catch (err) {
          /* best-effort */
        }
        set(e);
      });

      track.addEventListener('pointermove', function (e) {
        if (down) set(e);
      });

      function up() {
        down = false;
      }

      track.addEventListener('pointerup', up);
      track.addEventListener('pointercancel', up);
      track.addEventListener('lostpointercapture', up);
    };

    Turntable.prototype._bindScroll = function () {
      var self = this;
      var pin = this.dataset.pin ? document.querySelector(this.dataset.pin) : this.closest('[data-sd-pin]');
      if (!pin) return;

      this.progress = 0;

      onFrame(function (vw, vh) {
        var r = pin.getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) return;

        var span = r.height - vh;
        var p = span > 0 ? clamp(-r.top / span, 0, 1) : 0;
        self.progress = p;
        self.turn = p * (self.count - 1);
        self.paint();

        if (self.onProgress) self.onProgress(p, vw, vh);
      });
    };

    return Turntable;
  })();

  if (!window.customElements.get('sd-turntable')) {
    customElements.define('sd-turntable', Turntable);
  }

  /* ── homepage hero ───────────────────────────────────────────────────────
     The turntable drives the frame index; this adds the grayscale develop and
     the type drift on top of the same progress value.
     ──────────────────────────────────────────────────────────────────────── */

  var HERO_LS_MIN = -0.05; /* the design's resting tracking */
  var HERO_LS_MAX = 0.025; /* the design's fully-open tracking */
  var HERO_EDGE = 20; /* px of viewport margin the word must never cross */

  function initHero() {
    var hero = document.querySelector('[data-sd-hero]');
    if (!hero) return;

    var stage = hero.querySelector('sd-turntable');
    var word = hero.querySelector('[data-sd-hero-word]');
    var fore = hero.querySelector('[data-sd-hero-fore]');
    if (!stage) return;

    /* How far the tracking may open before STATEMENT clips at the viewport
       edges. This MUST NOT be derived from the word's live width: that width
       already contains the tracking set on the previous frame, which makes the
       calculation feed back on itself and the word visibly judder between wide
       and narrow. Instead measure the word twice at known tracking values to
       get an exact px-per-em slope, then treat tracking as a pure function of
       scroll progress. Re-measured only on resize and after the webfont lands,
       both of which genuinely change the metrics. */
    var room = 0;

    function measureRoom() {
      if (!word) return;

      var prevLetterSpacing = word.style.letterSpacing;
      var prevTransition = word.style.transition;
      word.style.transition = 'none';

      word.style.letterSpacing = HERO_LS_MIN + 'em';
      var narrow = word.scrollWidth;
      word.style.letterSpacing = HERO_LS_MAX + 'em';
      var wide = word.scrollWidth;

      word.style.letterSpacing = prevLetterSpacing;
      word.style.transition = prevTransition;

      var span = HERO_LS_MAX - HERO_LS_MIN;
      var slope = (wide - narrow) / span; /* px of width per em of tracking */
      if (slope <= 0) {
        room = 0;
        return;
      }

      var available = innerWidth - HERO_EDGE - narrow;
      room = clamp(available / slope, 0, span);
    }

    measureRoom();
    addEventListener('resize', measureRoom, { passive: true });
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(measureRoom);
    }

    function ready() {
      stage.onProgress = function (p) {
        /* develops from monochrome to full colour over the first ~38% */
        var dev = clamp(p * 2.6, 0, 1);
        stage.style.filter =
          'grayscale(' + (1 - dev).toFixed(2) + ') contrast(' + (1.06 - 0.06 * dev).toFixed(2) + ')';

        if (REDUCE) return;

        if (word) {
          word.style.transform = 'translateX(-50%) translateY(' + -p * 140 + 'px)';
          word.style.letterSpacing = (HERO_LS_MIN + p * room).toFixed(5) + 'em';
          word.style.opacity = (1 - p * 0.55).toFixed(2);
        }

        if (fore) {
          fore.style.transform = 'translateX(-50%) translateY(' + p * 80 + 'px)';
        }
      };
    }

    if (stage._ready) ready();
    else customElements.whenDefined('sd-turntable').then(ready);
  }

  /* ── cart ────────────────────────────────────────────────────────────────
     The bag count in the nav is live. Adding from the quick-view drawer or the
     buy panel posts to the real cart, then re-reads it.
     ──────────────────────────────────────────────────────────────────────── */

  SD.refreshBag = function () {
    var url = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js';
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        document.querySelectorAll('[data-sd-bag-count]').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        return cart;
      });
  };

  SD.addToCart = function (variantId, qty) {
    var url = (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: qty || 1 }] }),
    })
      .then(function (r) {
        return r.json().then(function (body) {
          if (!r.ok) throw body;
          return body;
        });
      })
      .then(function (body) {
        return SD.refreshBag().then(function () {
          return body;
        });
      });
  };

  /* "Size 30 — two left" is real merchandising information, so it reads from
     inventory where inventory is tracked. */
  SD.stockLine = function (variant, optionLabel) {
    var label = optionLabel ? optionLabel + ' ' + variant.option : variant.title;
    if (!variant.available) return label + ' — sold out';
    if (variant.tracked) {
      if (variant.qty === 1) return label + ' — one left';
      if (variant.qty === 2) return label + ' — two left';
      if (variant.qty > 0 && variant.qty <= 5) return label + ' — ' + variant.qty + ' left';
    }
    return label + ' — in stock';
  };

  /* ── boot ────────────────────────────────────────────────────────────── */

  function boot() {
    initCursor();
    initReveals();
    initMagnets();
    initParallax();
    initNav();
    initCurtain();
    initAccordion();
    initHero();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  /* re-scan after the theme editor re-renders a section */
  document.addEventListener('shopify:section:load', function () {
    initReveals();
    initAccordion();
    initHero();
  });

  SD.rescanReveals = initReveals;
})();
